import React, { useState, useEffect, useMemo } from "react";
import {
  inputWrapper,
  urlsForm,
  label,
  buttonsWrapper,
} from "../UserURLsDrawer/styles.css";
import {
  input,
  primaryButton,
  skeleton,
  focusOutlineVar,
  primaryBgVar,
} from "../../commons/styled/index.css";
import { useUserData } from "../../../context";
import { notifyError, notifySuccess } from "../../../helpers/notify";
import { useAddLocationPermissions } from "../../../VectorFlow/Services/MTA/MDM";
import UploadModal from "../../../VectorFlow/Pages/MTA/MDM/ViewModify/UploadModal";
import useView from "./useView";
import { getErrorLocationColumns, getLocationColumns } from "./View";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store/store";
import ErrorPermissions from "./ErrorPermissions";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import * as globalStyles from "../../../styles/global";

interface FormDataType {
  locationHierarchy1: string;
  locationHierarchy2: string;
  locationHierarchy3: string;
}

const AddLocationPermission = (props: { cb: () => void }) => {
  const { cb } = props;

  const { user } = useUserData();

  const themeUi = user.user.theme_ui;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const EnvConfig = useSelector((state: RootState) => state.mta.EnvConfig);
  const locationColumns = getLocationColumns(EnvConfig);

  const [formData, setFormData] = useState<FormDataType>({
    locationHierarchy1: "",
    locationHierarchy2: "",
    locationHierarchy3: "",
  });

  const {
    downloadFileName,
    setDownloadFileName,
    file,
    setFile,
    isUploadModalOpen,
    toggleUploadModal,
    onUpload,
    setUploadCallback,
    exportToExcel,
    RECORD_UPLOAD_LIMIT,
    showErrorRows,
    errorRowData,
  } = useView(locationColumns);

  const { mutateAsync: addLocationPermission } = useAddLocationPermissions();

  const errorLocationcolumn = getErrorLocationColumns(EnvConfig);
  const headerNameMap: Record<string, string> = {};
  locationColumns.forEach((col) => {
    headerNameMap[col.colId] = col.headerName;
  });
  const headersList = locationColumns
    ?.filter((col) => col.colId !== "id")
    .map((col) => col.headerName);
  useEffect(() => {
    setIsLoading(false);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const data = {
        permissionType: "Location",
        Headers: headersList,
        data: [{ ...formData }],
      } as any;
      const response = await addLocationPermission(data);
      const responseData = response?.data?.data;

      if (responseData?.errorCount === 1) {
        notifyError(responseData?.errors[0]?.rowData?.error);
      } else {
        notifySuccess(`${responseData?.inserted} record inserted successfully`);
      }
      cb();
    } catch (error) {
      console.error(error);
      notifyError("Server Went Unresponsive");
    }
  };

  const handleUpload = async () => {
    await onUpload(RECORD_UPLOAD_LIMIT);
    setUploadCallback(cb);
  };

  const isFormInvalid = useMemo((): boolean => {
    const { locationHierarchy1, locationHierarchy2, locationHierarchy3 } =
      formData;

    const filledValues = [
      locationHierarchy1,
      locationHierarchy2,
      locationHierarchy3,
    ].filter((v) => v !== "");
    const lowerCaseFilledValues = filledValues.map((v) => v.toLowerCase());

    if (new Set(lowerCaseFilledValues).size < filledValues.length) return true;
    if (!locationHierarchy1 && !locationHierarchy2 && !locationHierarchy3)
      return true;
    if (!locationHierarchy1 && locationHierarchy2) return true;
    if (!locationHierarchy2 && locationHierarchy3) return true;
    return false;
  }, [formData]);

  if (isLoading) {
    return (
      <form className={urlsForm}>
        <div style={{ display: "flex", height: 30, gap: 20 }}>
          <div
            className={skeleton}
            style={{ height: "100%", flex: 1, width: "100%" }}
          />
          <div
            className={skeleton}
            style={{ height: "100%", flex: 1, width: "100%" }}
          />
        </div>
        <div
          className={skeleton}
          style={{ height: 30, width: "100%", marginTop: 20 }}
        />
        <div
          className={skeleton}
          style={{ height: 30, width: "100%", marginTop: 20 }}
        />

        <div
          className={skeleton}
          style={{ height: 80, width: "100%", marginTop: 20 }}
        />
        <div
          className={buttonsWrapper}
          style={{
            alignItems: "flex-end",
            justifyContent: "flex-end",
            flex: 10,
          }}
        >
          <div className={skeleton} style={{ height: 30, width: "100px" }} />
        </div>
      </form>
    );
  }
  if (showErrorRows)
    return (
      <ErrorPermissions
        columnDefs={errorLocationcolumn}
        rowData={errorRowData}
      />
    );
  const focusColor =
    globalStyles.chooseThemeColor[themeUi]?.color4 ?? "transparent";
  const bgColor =
    globalStyles.chooseThemeColor[themeUi]?.color5 ?? "transparent";

  return (
    <>
      <form className={urlsForm} onSubmit={handleSubmit}>
        <div style={{ display: "flex" }}>
          <div className={inputWrapper}>
            <label className={label} htmlFor="locationHierarchy1">
              {locationColumns[1]?.headerName}
            </label>
            <input
              className={input}
              type={"text"}
              required
              name="locationHierarchy1"
              placeholder={`Any ${locationColumns[1]?.headerName}`}
              style={assignInlineVars({
                [focusOutlineVar]: focusColor,
              })}
              onChange={handleChange}
              maxLength={190}
            />
          </div>

          <div className={inputWrapper} style={{ marginLeft: "10px" }}>
            <label className={label} htmlFor="locationHierarchy2">
              {locationColumns[2]?.headerName}
            </label>
            <input
              className={input}
              type={"text"}
              name="locationHierarchy2"
              placeholder={`Any ${locationColumns[2]?.headerName}`}
              style={assignInlineVars({
                [focusOutlineVar]: focusColor,
              })}
              onChange={handleChange}
              maxLength={190}
            />
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <div className={inputWrapper} style={{ marginLeft: "10px" }}>
            <label className={label} htmlFor="locationHierarchy3">
              {locationColumns[3]?.headerName}
            </label>
            <input
              className={input}
              type={"text"}
              name="locationHierarchy3"
              placeholder={`Any ${locationColumns[3]?.headerName}`}
              style={assignInlineVars({
                [focusOutlineVar]: focusColor,
              })}
              onChange={handleChange}
              maxLength={190}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
            flex: 10,
            gap: 20,
          }}
        >
          <button
            className={primaryButton}
            type="button"
            style={assignInlineVars({
              [focusOutlineVar]: focusColor,
              [primaryBgVar]: bgColor,  
            })}
            onClick={() => toggleUploadModal(true)}
          >
            Bulk Upload
          </button>
          <button
            className={primaryButton}
            disabled={isFormInvalid || isSubmitting}
            style={assignInlineVars({
              [focusOutlineVar]: focusColor,
              [primaryBgVar]: bgColor,  
            })}
          >
            Add Permission
          </button>
        </div>
      </form>
      {isUploadModalOpen && (
        <UploadModal
          header={"Upload Location Permissions"}
          openModal={isUploadModalOpen}
          onCloseModal={() => {
            setFile(undefined);
            toggleUploadModal(false);
          }}
          onDownload={() => exportToExcel(true)}
          onUpload={async () => {
            await handleUpload();
          }}
          inputText={downloadFileName}
          setInputText={setDownloadFileName}
          file={file}
          setFile={setFile}
          uploadButtonStatus={false}
        />
      )}
    </>
  );
};

export default AddLocationPermission;
